import tutorialsData from '../data/tutorials.json';

// Google Sheets configuration
const GOOGLE_SHEET_ID = '13XfnFUfO67t1g_zdD87IAyrS0yC9zdDQUlltnjt4Rk8';
const GOOGLE_SHEET_NAME = 'Sheet1';

class TutorialService {
  constructor() {
    this.data = tutorialsData;
    // Removed apiBase - now using Google Sheets as primary source
    this.userPinnedVideos = JSON.parse(localStorage.getItem('userPinnedVideos') || '[]');
  }

  // Fetch tutorials from Google Sheets
  async fetchFromGoogleSheets() {
    try {
      // Use the public JSON export endpoint
      const jsonUrl = `https://docs.google.com/spreadsheets/d/${GOOGLE_SHEET_ID}/gviz/tq?tqx=out:json&sheet=${GOOGLE_SHEET_NAME}`;
      
      const response = await fetch(jsonUrl);
      if (!response.ok) {
        throw new Error(`Failed to fetch sheet: ${response.status}`);
      }
      
      const text = await response.text();
      
      // Google Sheets wraps the JSON in: google.visualization.Query.setResponse(...)
      // We need to extract just the JSON object
      let jsonText = text.trim();
      
      // Method 1: Try to extract from setResponse wrapper
      if (jsonText.includes('google.visualization.Query.setResponse')) {
        // Find the opening parenthesis
        const startParen = jsonText.indexOf('(');
        if (startParen !== -1) {
          // Find the matching closing parenthesis by counting
          let parenCount = 0;
          let endParen = -1;
          let inString = false;
          let escapeNext = false;
          
          for (let i = startParen; i < jsonText.length; i++) {
            const char = jsonText[i];
            
            if (escapeNext) {
              escapeNext = false;
              continue;
            }
            
            if (char === '\\') {
              escapeNext = true;
              continue;
            }
            
            if (char === '"' && !escapeNext) {
              inString = !inString;
              continue;
            }
            
            if (!inString) {
              if (char === '(') parenCount++;
              if (char === ')') {
                parenCount--;
                if (parenCount === 0) {
                  endParen = i;
                  break;
                }
              }
            }
          }
          
          if (endParen !== -1) {
            jsonText = jsonText.substring(startParen + 1, endParen).trim();
          }
        }
      }
      
      // Method 2: If still wrapped, try to find the JSON object boundaries
      if (!jsonText.startsWith('{')) {
        const firstBrace = jsonText.indexOf('{');
        if (firstBrace !== -1) {
          // Find the matching closing brace
          let braceCount = 0;
          let endBrace = -1;
          let inString = false;
          let escapeNext = false;
          
          for (let i = firstBrace; i < jsonText.length; i++) {
            const char = jsonText[i];
            
            if (escapeNext) {
              escapeNext = false;
              continue;
            }
            
            if (char === '\\') {
              escapeNext = true;
              continue;
            }
            
            if (char === '"' && !escapeNext) {
              inString = !inString;
              continue;
            }
            
            if (!inString) {
              if (char === '{') braceCount++;
              if (char === '}') {
                braceCount--;
                if (braceCount === 0) {
                  endBrace = i;
                  break;
                }
              }
            }
          }
          
          if (endBrace !== -1) {
            jsonText = jsonText.substring(firstBrace, endBrace + 1);
          }
        }
      }
      
      // Try to parse the JSON
      let data;
      try {
        data = JSON.parse(jsonText);
      } catch (parseError) {
        console.error('JSON parse error details:', {
          error: parseError.message,
          position: parseError.message.match(/position (\d+)/)?.[1],
          preview: jsonText.substring(0, 500),
          length: jsonText.length
        });
        throw new Error(`JSON parse error: ${parseError.message}. Response length: ${text.length}, Extracted length: ${jsonText.length}`);
      }
      
      if (!data.table || !data.table.rows) return [];
      
      const rows = data.table.rows;
      if (rows.length === 0) return [];
      
      // Get headers from first row
      const headers = data.table.cols.map(col => col.label || '');
      if (headers.length === 0 || !headers[0]) return [];
      
      const tutorials = [];
      
      for (let i = 0; i < rows.length; i++) {
        const row = rows[i];
        if (!row.c || row.c.length === 0) continue;
        
        const tutorial = {};
        let hasData = false;
        
        headers.forEach((header, index) => {
          const cell = row.c[index];
          const value = cell ? (cell.v || '') : '';
          
          if (value) hasData = true;
          
          // Convert string values to appropriate types
          if (header === 'views' || header === 'likes') {
            tutorial[header] = parseInt(value) || 0;
          } else if (header === 'featured' || header === 'pinned' || header === 'isActive') {
            tutorial[header] = value === true || value === 'TRUE' || value === 'true' || value === '1';
          } else {
            tutorial[header] = value;
          }
        });
        
        if (!hasData) continue; // Skip empty rows
        
        // Use id or _id
        if (!tutorial._id && tutorial.id) {
          tutorial._id = tutorial.id;
        } else if (tutorial._id && !tutorial.id) {
          tutorial.id = tutorial._id;
        }
        
        // Only include active tutorials
        if (tutorial.isActive !== false) {
          tutorials.push(tutorial);
        }
      }
      
      return tutorials;
    } catch (error) {
      console.error('Error fetching from Google Sheets:', error);
      return null;
    }
  }

  // Get all videos with optional filtering
  async getVideos(options = {}) {
    try {
      // Try to fetch from Google Sheets first, then local data fallback
      let videos = [];
      
      // Try Google Sheets first (primary source)
      const sheetsVideos = await this.fetchFromGoogleSheets();
      if (sheetsVideos && sheetsVideos.length > 0) {
        videos = sheetsVideos;
        console.log('✅ Loaded tutorials from Google Sheets:', videos.length);
      } else {
        // Fallback to local data only (no API call)
        console.log('⚠️ Google Sheets not available, using local fallback data');
        videos = [...this.data.videos];
      }

      // Add user pinning information
      videos = videos.map(video => ({
        ...video,
        isUserPinned: this.userPinnedVideos.includes(video._id || video.id)
      }));

      // Filter by category
      if (options.category) {
        videos = videos.filter(video => video.category === options.category);
      }

      // Filter by featured
      if (options.featured) {
        videos = videos.filter(video => video.featured);
      }

      // Sort by priority: User Pinned (max 3) > Admin Featured > Creation Date
      videos.sort((a, b) => {
        const aPinned = a.isUserPinned;
        const bPinned = b.isUserPinned;
        const aFeatured = a.featured;
        const bFeatured = b.featured;

        // User pinned videos take highest priority
        if (aPinned && !bPinned) return -1;
        if (!aPinned && bPinned) return 1;

        // Then admin featured videos
        if (aFeatured && !bFeatured) return -1;
        if (!aFeatured && bFeatured) return 1;

        // Finally by creation date
        return new Date(b.createdAt) - new Date(a.createdAt);
      });

      // Limit results
      if (options.limit) {
        videos = videos.slice(0, options.limit);
      }

      return {
        success: true,
        videos,
        total: videos.length
      };
    } catch (error) {
      console.error('Error fetching videos:', error);
      return {
        success: false,
        error: error.message,
        videos: []
      };
    }
  }

  // Get video by ID
  async getVideoById(id) {
    try {
      const video = this.data.videos.find(v => v.id === id);
      if (!video) {
        return {
          success: false,
          error: 'Video not found'
        };
      }
      return {
        success: true,
        video
      };
    } catch (error) {
      console.error('Error fetching video:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  // Get categories
  async getCategories() {
    try {
      return {
        success: true,
        categories: this.data.categories
      };
    } catch (error) {
      console.error('Error fetching categories:', error);
      return {
        success: false,
        error: error.message,
        categories: []
      };
    }
  }

  // Search videos
  async searchVideos(query) {
    try {
      const searchTerm = query.toLowerCase();
      const videos = this.data.videos.filter(video => 
        video.title.toLowerCase().includes(searchTerm) ||
        video.description.toLowerCase().includes(searchTerm) ||
        video.category.toLowerCase().includes(searchTerm)
      );

      return {
        success: true,
        videos,
        total: videos.length,
        query
      };
    } catch (error) {
      console.error('Error searching videos:', error);
      return {
        success: false,
        error: error.message,
        videos: []
      };
    }
  }

  // Get user pinned videos
  async getUserPinnedVideos() {
    const allVideos = await this.getVideos();
    return {
      success: true,
      videos: allVideos.videos.filter(video => video.isUserPinned),
      total: this.userPinnedVideos.length
    };
  }

  // Pin a video for the current user (max 3)
  async pinVideo(videoId) {
    try {
      if (this.userPinnedVideos.length >= 3) {
        return {
          success: false,
          error: 'You can only pin up to 3 videos. Please unpin another video first.'
        };
      }

      if (!this.userPinnedVideos.includes(videoId)) {
        this.userPinnedVideos.push(videoId);
        localStorage.setItem('userPinnedVideos', JSON.stringify(this.userPinnedVideos));
      }

      return { success: true };
    } catch (error) {
      console.error('Error pinning video:', error);
      return { success: false, error: error.message };
    }
  }

  // Unpin a video
  async unpinVideo(videoId) {
    try {
      this.userPinnedVideos = this.userPinnedVideos.filter(id => id !== videoId);
      localStorage.setItem('userPinnedVideos', JSON.stringify(this.userPinnedVideos));
      return { success: true };
    } catch (error) {
      console.error('Error unpinning video:', error);
      return { success: false, error: error.message };
    }
  }

  // Get pinned videos count
  getPinnedCount() {
    return this.userPinnedVideos.length;
  }

  // Get featured videos
  async getFeaturedVideos() {
    return this.getVideos({ featured: true });
  }

  // Update video views (for analytics)
  async updateVideoViews(videoId) {
    try {
      const video = this.data.videos.find(v => v.id === videoId);
      if (video) {
        video.views = (video.views || 0) + 1;
        // In the future, this would send to your API
        console.log(`Updated views for video ${videoId}: ${video.views}`);
      }
      return { success: true };
    } catch (error) {
      console.error('Error updating video views:', error);
      return { success: false, error: error.message };
    }
  }

  // Note: API methods removed - now using Google Sheets as primary source
}

export default new TutorialService();
